"use client";

import { useAuth } from "@/components/AuthContext";
import { supabase } from "@/lib/supabaseClient";
import { ExternalLink, Loader2, Trash2 } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import posthog from "posthog-js";
import { useState } from "react";

export function DeleteAccountPanel() {
  const { clearAuthState, isLoading, openAuthModal, user } = useAuth();
  const router = useRouter();
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (confirmation !== "DELETE") return;

    setIsDeleting(true);
    setError(null);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error("Your session expired. Sign in again before deleting your account.");
      }

      const response = await fetch("/api/account", {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ confirmation: "DELETE" }),
      });
      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(payload?.error || "Account deletion could not be completed.");
      }

      try {
        posthog.reset();
        posthog.opt_out_capturing();
      } catch {
        // Analytics cleanup is already requested by the server.
      }

      await supabase.auth.signOut({ scope: "local" }).catch(() => undefined);
      await clearAuthState();
      router.replace("/delete-account?deleted=1");
      router.refresh();
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : String(deleteError));
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-28 items-center justify-center text-white/60">
        <Loader2 className="mr-2 h-4 w-4 animate-spin text-orange-300" />
        Checking your account...
      </div>
    );
  }

  if (!user) {
    return (
      <div className="space-y-4">
        <p className="text-sm leading-6 text-white/65">
          Sign in with the account you want to delete. You can complete the process here without
          reinstalling the mobile app.
        </p>
        <button
          type="button"
          onClick={() => openAuthModal("signin")}
          className="inline-flex h-11 items-center justify-center rounded-md bg-orange-500 px-4 text-sm font-bold text-white transition-colors hover:bg-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-300/60"
        >
          Sign in to continue
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <p className="text-sm leading-6 text-white/70">
          Deleting your account permanently removes your ChickyTutor profile, learning history,
          saved practice data, uploaded learning media, and processor-held customer data.
        </p>
        <p className="text-sm leading-6 text-amber-200/85">
          This does not cancel a Google Play or App Store subscription. Cancel it separately before
          deleting your account to prevent future renewals.
        </p>
      </div>

      <div className="flex flex-wrap gap-3">
        <a
          href="https://play.google.com/store/account/subscriptions"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 text-sm font-semibold text-orange-300 hover:text-orange-200"
        >
          Google Play subscriptions
          <ExternalLink className="h-4 w-4" />
        </a>
        <Link
          href="/subscription"
          className="text-sm font-semibold text-orange-300 hover:text-orange-200"
        >
          ChickyTutor subscription details
        </Link>
      </div>

      {!isConfirming ? (
        <button
          type="button"
          onClick={() => setIsConfirming(true)}
          className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-red-400/40 bg-red-500/10 px-4 text-sm font-bold text-red-200 transition-colors hover:bg-red-500/20 focus:outline-none focus:ring-2 focus:ring-red-300/50"
        >
          <Trash2 className="h-4 w-4" />
          Delete account
        </button>
      ) : (
        <div className="space-y-4 border-t border-white/10 pt-5">
          <label
            htmlFor="delete-account-confirmation"
            className="block text-sm font-semibold text-white"
          >
            Type <span className="font-mono text-red-200">DELETE</span> to confirm
          </label>
          <input
            id="delete-account-confirmation"
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            autoComplete="off"
            disabled={isDeleting}
            className="h-11 w-full max-w-sm rounded-md border border-white/15 bg-black/20 px-3 text-sm text-white outline-none transition-colors placeholder:text-white/30 focus:border-red-300/60"
            placeholder="DELETE"
          />

          {error ? (
            <p role="alert" className="max-w-xl text-sm leading-6 text-red-200">
              {error}
            </p>
          ) : null}

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => void handleDelete()}
              disabled={confirmation !== "DELETE" || isDeleting}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-red-600 px-4 text-sm font-bold text-white transition-colors hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-45"
            >
              {isDeleting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              {isDeleting ? "Deleting..." : "Permanently delete account"}
            </button>
            <button
              type="button"
              onClick={() => {
                setIsConfirming(false);
                setConfirmation("");
                setError(null);
              }}
              disabled={isDeleting}
              className="h-11 rounded-md border border-white/15 px-4 text-sm font-semibold text-white/75 hover:bg-white/[0.05] disabled:opacity-45"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
